import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, fmt, fmtDate, getUser, usdHint } from '../api';
import { Loading, ErrorBox, Modal, Field, Input, Eyebrow, useToast } from '../ui';
import { Icons } from '../icons';

const SYM: Record<string, string> = { USD: '$', CNY: '¥', UZS: "so'm" };

/** Narxni valyuta belgisi bilan; USD bo'lmasa yoniga taxminiy $ qiymati. */
function price(v: any, cur?: string) {
  if (v == null) return '—';
  const c = (cur || 'USD').toUpperCase();
  const hint = usdHint(v, c, 4);
  return (
    <span>
      {c === 'USD' ? '$' : ''}{fmt(v, c === 'UZS' ? 0 : 4)}{c !== 'USD' ? ' ' + (SYM[c] || c) : ''}
      {hint && <span className="muted" style={{ marginLeft: 6, fontSize: 11.5 }}>{hint}</span>}
    </span>
  );
}

export default function PriceHistory() {
  const user = getUser();
  const canAdd = ['admin', 'rastamojka'].includes(user?.role);
  const [add, setAdd] = useState<any>(null);
  const [list, setList] = useState<any[] | null>(null);
  const [all, setAll] = useState<any[]>([]);   // filtr ro'yxatlarini to'ldirish uchun
  const [onlyIncreased, setOnlyIncreased] = useState(false);
  const [search, setSearch] = useState('');
  const [brand, setBrand] = useState('');
  const [group, setGroup] = useState('');
  const [err, setErr] = useState('');

  const load = () => {
    setList(null); setErr('');
    const qs = new URLSearchParams();
    if (onlyIncreased) qs.set('onlyIncreased', 'true');
    if (search) qs.set('search', search);
    if (brand) qs.set('brand', brand);
    if (group) qs.set('group', group);
    api.get('/price-history' + (qs.toString() ? '?' + qs.toString() : ''))
      .then((d) => { setList(d); if (!brand && !group && !search && !onlyIncreased) setAll(d); })
      .catch((e) => setErr(e.message));
  };
  useEffect(() => { load(); }, [onlyIncreased, brand, group]);

  const brands = [...new Set(all.map((r: any) => r.brand).filter(Boolean))] as string[];
  const groups = [...new Set(all.map((r: any) => r.group).filter(Boolean))] as string[];

  return (
    <div>
      <div className="page-head">
        <div><h1 className="page-title">Narxlar dinamikasi</h1><p className="page-sub">
            <b>Prixod narxi</b> (so'mda) — har kirimdagi qiymatlarning <b>eng qimmati</b>;
            shartnoma narxi aynan shundan chiqadi (<b>× 1,1 × 1,12</b>).
            Har yuk bo'yicha alohida qiymatlarni ko'rish uchun —{' '}
            <Link to="/prixod-matrix" style={{ color: 'var(--accent)' }}>Prixod tarixi</Link> bo'limi.
          </p></div>
        {canAdd && (
          <button className="btn" onClick={() => setAdd({ mode: 'excel' })}>
            <Icons.upload size={16} /> Prixod narxlarini qo'shish
          </button>
        )}
      </div>
      <form className="filters" onSubmit={(e) => { e.preventDefault(); load(); }}>
        <input className="search" placeholder="Artikul / nom bo'yicha qidirish" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="input" style={{ width: 150 }} value={brand} onChange={(e) => setBrand(e.target.value)}>
          <option value="">Barcha brendlar</option>
          {brands.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <select className="input" style={{ width: 220 }} value={group} onChange={(e) => setGroup(e.target.value)}>
          <option value="">Barcha rastamojka guruhlari</option>
          {groups.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <label className="chip"><input type="checkbox" checked={onlyIncreased} onChange={(e) => setOnlyIncreased(e.target.checked)} /> Faqat narxi oshganlar</label>
        <button className="btn">Qidirish</button>
        {(search || brand || group || onlyIncreased) && (
          <button type="button" className="btn btn-ghost" onClick={() => { setSearch(''); setBrand(''); setGroup(''); setOnlyIncreased(false); }}>Tozalash</button>
        )}
        <span className="spacer" />
        {list && <span className="muted">{list.length} ta mahsulot</span>}
      </form>
      {err ? <ErrorBox text={err} onRetry={load} /> : !list ? <Loading /> : list.length === 0 ? <div className="card"><p className="muted">Hali rastamojka tasdiqlanmagan — tarix bo'sh</p></div> : (
        <div className="table-wrap">
          <table>
            <thead><tr>
              <th>Artikul</th><th>Nomi</th><th>Guruh</th><th className="num">Kirim</th>
              <th className="num" title="Umumiy etalon: shu paytgacha yig'ilgan eng qimmat narx (so'm, 1 dona)">
                Prixod narxi<div className="th-sub">etalon · eng qimmat</div>
              </th>
              <th className="num" title="Prixod narxi × 1,1 × 1,12">
                Shartnoma bera olamiz<div className="th-sub">prixod × 1,1 × 1,12</div>
              </th>
              <th className="num">Invoys narx</th><th>Oxirgi sana</th>
            </tr></thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.productId}>
                  <td><Link to={'/products/' + r.productId} className="code" style={{ color: 'var(--accent)' }}>{r.article}</Link></td>
                  <td>{r.name}</td><td>{r.group || '—'}</td>
                  <td className="num">{r.count}</td>
                  {/* Etalon — eng qimmat narx; shartnoma shundan hisoblanadi */}
                  <td className="num" style={{ fontWeight: 700 }}>
                    {r.prixodPrice == null ? <span className="muted">—</span> : fmt(r.prixodPrice, 2)}
                  </td>
                  <td className="num" style={{ fontWeight: 700, color: 'var(--accent)' }}>
                    {r.contractPrice == null ? <span className="muted">—</span> : fmt(r.contractPrice, 2)}
                  </td>
                  <td className="num muted">{price(r.lastPrice, r.currency)}</td>
                  <td>{fmtDate(r.lastDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {add && <PrixodModal onClose={() => setAdd(null)} onDone={() => { setAdd(null); load(); }} />}
    </div>
  );
}

/**
 * Prixod narxlarini qo'shish: «Mahsulotlar tannarxi» Excel fayli yoki bitta
 * mahsulotga qo'lda.
 *
 * <p>Qo'lda kiritish partiya tannarxi <b>umumiy</b> berilgan hollar uchun
 * (masalan «Elektr shit», 3 060 dona, 12 519,94 so'm/dona) — qaysi rangdagi
 * shitga tegishli ekani ajratilmagan bo'ladi.
 */
function PrixodModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [tab, setTab] = useState<'excel' | 'manual'>('excel');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [sheet, setSheet] = useState('Asosiy');
  const [one, setOne] = useState<any>({ article: '', prixodUzs: '', date: '', qty: '', supplierName: '' });
  const fileRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const send = async (f: File, dryRun: boolean) => {
    setBusy(true);
    try {
      const r = await api.upload('/onec/prixod/import', f, { dryRun: String(dryRun), sheet });
      if (dryRun) { setFile(f); setPreview(r); }
      else { toast(`${r.willWrite} ta narx yozuvi qo'shildi`); onDone(); }
    } catch (e: any) { toast(e.message, true); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Prixod narxlarini qo'shish" onClose={onClose} large>
      <div className="tabs">
        <button className={'tab' + (tab === 'excel' ? ' active' : '')} onClick={() => setTab('excel')}>Excel fayldan</button>
        <button className={'tab' + (tab === 'manual' ? ' active' : '')} onClick={() => setTab('manual')}>Bitta mahsulotga</button>
      </div>

      {tab === 'excel' ? (
        <>
          <p className="muted" style={{ marginTop: 0 }}>
            «Mahsulotlar tannarxi» fayli. Varaqda ustunlar shunday bo'lishi kerak:
            <b> Mahsulot | Artikul</b>, keyin har partiya uchun to'rttadan —
            <b> Invoys narx | Xarajat | Tannarx | Prixod : KK.OO.YYYY</b>. Sana oxirgi ustun
            sarlavhasidan olinadi, qiymat ham o'sha ustundan.
          </p>
          <div className="filters" style={{ marginBottom: 10 }}>
            <input className="input" style={{ width: 160 }} value={sheet} onChange={(e) => setSheet(e.target.value)} placeholder="Varaq nomi" />
            <input ref={fileRef} type="file" accept=".xlsx" hidden
              onChange={(e) => { const f = e.target.files?.[0]; if (f) send(f, true); if (fileRef.current) fileRef.current.value = ''; }} />
            <button className="btn btn-primary" disabled={busy} onClick={() => fileRef.current?.click()}>
              <Icons.upload size={16} /> {preview ? 'Boshqa fayl' : 'Faylni tanlash'}
            </button>
          </div>

          {busy && <Loading rows={2} />}

          {preview && !busy && (
            <div className="card" style={{ padding: 12 }}>
              <Eyebrow title="Nima yoziladi" desc={`${preview.willWrite} ta narx yozuvi, ${preview.dateColumns} ta partiya sanasi`} />
              <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                Sanalar: {(preview.dates || []).join(', ')}
              </div>
              {!!preview.unknownArticles?.length && (
                <div className="muted" style={{ fontSize: 12, marginTop: 8, color: 'var(--warn)' }}>
                  Katalogda topilmadi ({preview.unknownArticles.length} ta): {preview.unknownArticles.slice(0, 20).join(', ')}
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <button className="btn" onClick={onClose}>Bekor</button>
            <button className="btn btn-primary" disabled={busy || !file || !preview?.willWrite}
              onClick={() => file && send(file, false)}>
              {busy ? 'Yuklanmoqda…' : `Tasdiqlash va saqlash (${preview?.willWrite ?? 0})`}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="muted" style={{ marginTop: 0 }}>
            Partiya tannarxi umumiy berilgan bo'lsa, uni bitta mahsulotga yozib qo'yiladi.
          </p>
          <div className="grid-2">
            <Field label="Artikul *"><Input value={one.article} onChange={(e) => setOne({ ...one, article: e.target.value })} placeholder="16-6-10-0004" /></Field>
            <Field label="Prixod narxi, so'm *"><Input value={one.prixodUzs} onChange={(e) => setOne({ ...one, prixodUzs: e.target.value })} placeholder="12519.94" /></Field>
          </div>
          <div className="grid-2">
            <Field label="Sana"><Input type="date" value={one.date} onChange={(e) => setOne({ ...one, date: e.target.value })} /></Field>
            <Field label="Miqdor (ixtiyoriy)"><Input value={one.qty} onChange={(e) => setOne({ ...one, qty: e.target.value })} placeholder="3060" /></Field>
          </div>
          <Field label="Yetkazib beruvchi (ixtiyoriy)">
            <Input value={one.supplierName} onChange={(e) => setOne({ ...one, supplierName: e.target.value })} placeholder="YIWU YUYUE IMPORT AND EXPORT" />
          </Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <button className="btn" onClick={onClose}>Bekor</button>
            <button className="btn btn-primary" disabled={busy || !one.article || !one.prixodUzs}
              onClick={async () => {
                setBusy(true);
                try {
                  const r = await api.post('/onec/prixod/set', one);
                  toast(`${r.product.article} — ${fmt(r.prixodUzs)} so'm qo'shildi`);
                  onDone();
                } catch (e: any) { toast(e.message, true); }
                finally { setBusy(false); }
              }}>Saqlash</button>
          </div>
        </>
      )}
    </Modal>
  );
}
